/**
 * DiscoveryAgent — conversational B2B lead qualifier.
 *
 * Route: /discovery
 *
 * Replaces the traditional "10-field contact form" with a 5-step
 * questionnaire that grades every visitor on BANT (Budget / Authority /
 * Need / Timeline) server-side and routes hot leads to the founder's
 * Calendly while sending cold leads to self-serve pricing.
 *
 * Zero LLM cost — all branching is client-side; scoring is rule-based
 * on the backend.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Building2, Users, Wrench, Calendar, ArrowRight, ArrowLeft, CheckCircle2, Sparkles, ExternalLink } from "lucide-react";

const STEPS = [
  {
    key: "doors",
    q: "How many rental units do you manage today?",
    options: [
      { v: "1_5",       label: "1 – 5 units",       icon: Building2 },
      { v: "6_20",      label: "6 – 20 units",      icon: Building2 },
      { v: "21_100",    label: "21 – 100 units",    icon: Building2 },
      { v: "100_plus",  label: "100+ units",        icon: Building2 },
    ],
  },
  {
    key: "role",
    q: "What best describes you?",
    options: [
      { v: "landlord",         label: "Individual landlord",           icon: Users },
      { v: "property_manager", label: "Property manager",              icon: Users },
      { v: "portfolio_owner",  label: "Portfolio owner / investor",    icon: Users },
      { v: "tenant",           label: "I'm a tenant, not a landlord",  icon: Users },
    ],
  },
  {
    key: "pain",
    q: "What's the biggest thing eating your time right now?",
    options: [
      { v: "rent_tracking",    label: "Chasing rent + reconciling payments", icon: Wrench },
      { v: "leases",           label: "Writing / updating leases",           icon: Wrench },
      { v: "tax_reports",      label: "CRA tax reports (T776 / NR4)",        icon: Wrench },
      { v: "tenant_screening", label: "Screening applicants",                icon: Wrench },
      { v: "maintenance",      label: "Coordinating maintenance",            icon: Wrench },
      { v: "all",              label: "Honestly, all of the above",          icon: Sparkles },
    ],
  },
  {
    key: "timeline",
    q: "When would you like to have this handled?",
    options: [
      { v: "now",          label: "This week — I'm ready",     icon: Calendar },
      { v: "30d",          label: "Within the next 30 days",    icon: Calendar },
      { v: "90d",          label: "Sometime in the next 90 days", icon: Calendar },
      { v: "just_looking", label: "Just researching for now",   icon: Calendar },
    ],
  },
  {
    key: "budget_ok",
    q: "Central Rentals starts at CA$35 / month. Is that within your budget?",
    options: [
      { v: true,   label: "Yes, that works",                icon: CheckCircle2 },
      { v: false,  label: "Not right now — show me free options", icon: CheckCircle2 },
    ],
  },
];

export default function DiscoveryAgent() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const total = STEPS.length;
  const cur = STEPS[step];

  const choose = async (value) => {
    const next = { ...answers, [cur.key]: value };
    setAnswers(next);
    if (step + 1 < total) {
      setStep(step + 1);
      return;
    }
    // Last question — submit for scoring.
    setSubmitting(true);
    try {
      const { data } = await api.post("/discovery/qualify", next);
      setResult(data);
    } catch {
      // Fall back to self-serve on any backend hiccup so the user is
      // never stranded.
      setResult({
        tier: "warm",
        next_step: {
          kind: "self_serve",
          url: "/pricing",
          cta: "See pricing",
          reason: "Take a look — most landlords are set up within 20 minutes.",
        },
      });
    } finally { setSubmitting(false); }
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  if (result) return <DiscoveryResult result={result} answers={answers} />;

  return (
    <div className="min-h-[70vh] bg-gradient-to-br from-slate-50 to-emerald-50/50 py-16 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700 mb-2">
          Central Rentals · Discovery
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900 leading-tight">
          Let's find the right fit — 5 quick questions.
        </h1>
        <p className="text-slate-600 mt-2">No sales pitch. Just a smarter next step based on your answers.</p>

        {/* Progress bar */}
        <div className="mt-8 mb-6">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-2">
            <span>Question {step + 1} of {total}</span>
            <span>{Math.round(((step + 1) / total) * 100)}%</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-600 transition-all duration-300 rounded-full" style={{ width: `${((step + 1) / total) * 100}%` }} />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-8">
          <h2 className="font-display text-xl md:text-2xl font-bold text-slate-900 mb-6">
            {cur.q}
          </h2>
          <div className="space-y-2">
            {cur.options.map((opt) => {
              const Icon = opt.icon;
              return (
                <button
                  key={String(opt.v)}
                  type="button"
                  onClick={() => choose(opt.v)}
                  disabled={submitting}
                  data-testid={`discovery-option-${cur.key}-${opt.v}`}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 text-left font-semibold text-slate-900 transition-colors disabled:opacity-50"
                >
                  <Icon className="w-5 h-5 text-emerald-600 flex-none" />
                  <span className="flex-1">{opt.label}</span>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </button>
              );
            })}
          </div>
          {step > 0 && (
            <button
              type="button"
              onClick={back}
              data-testid="discovery-back"
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}
          {submitting && (
            <div className="mt-4 text-sm text-emerald-700 font-semibold">Analysing your answers…</div>
          )}
        </div>
      </div>
    </div>
  );
}

function DiscoveryResult({ result, answers }) {
  const step = result?.next_step || {};
  const tier = result?.tier || "warm";
  const isDemoRoute = step.kind === "book_demo";
  const isExternalCalendly = /^https?:\/\//i.test(step.url || "");
  return (
    <div className="min-h-[70vh] bg-gradient-to-br from-emerald-50 via-white to-emerald-50/60 py-16 px-6">
      <div className="max-w-2xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-600 text-white text-xs font-bold uppercase tracking-widest">
          <CheckCircle2 className="w-3.5 h-3.5" /> Personalised for you
        </div>
        <h1 className="font-display mt-4 text-3xl md:text-4xl font-black text-slate-900 leading-tight">
          Based on your answers, here&apos;s the smartest next step.
        </h1>
        <p className="text-slate-600 mt-3 max-w-lg mx-auto">{step.reason}</p>

        <div className="mt-8 bg-white border border-emerald-200 rounded-2xl shadow-md p-8" data-testid={`discovery-result-${tier}`}>
          <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700 mb-2">
            {isDemoRoute ? "Live founder demo" : "Self-serve"}
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-5">
            {step.cta}
          </h2>
          {isExternalCalendly ? (
            <a
              href={step.url}
              target="_blank"
              rel="noreferrer"
              data-testid="discovery-primary-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm transition-colors"
            >
              {step.cta} <ExternalLink className="w-4 h-4" />
            </a>
          ) : (
            <Link
              to={step.url}
              data-testid="discovery-primary-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm transition-colors"
            >
              {step.cta} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>

        {/* Contextual case-study callout — matched to the pain answer */}
        {answers?.pain && <PainCallout pain={answers.pain} />}
      </div>
    </div>
  );
}

const PAIN_CASE_STUDIES = {
  rent_tracking:  { title: "How Halifax landlord Marta stopped chasing rent in 3 weeks",   url: "/case-studies/rent-tracking" },
  leases:         { title: "Ottawa property manager cut lease-drafting from 2 hours to 6 minutes", url: "/case-studies/leases" },
  tax_reports:    { title: "How a Calgary landlord filed T776 in under 20 minutes with Central Rentals", url: "/case-studies/tax" },
  tenant_screening:{ title: "Toronto owner filled a vacancy in 6 days — with the right tenant", url: "/case-studies/screening" },
  maintenance:    { title: "Vancouver portfolio saves 8 hrs/week on maintenance coordination", url: "/case-studies/maintenance" },
  all:            { title: "How a 42-unit Winnipeg operator runs the whole portfolio in 3 hours a week", url: "/case-studies/portfolio" },
};

function PainCallout({ pain }) {
  const cs = PAIN_CASE_STUDIES[pain];
  if (!cs) return null;
  return (
    <div className="mt-6 p-5 bg-slate-50 border border-slate-200 rounded-xl text-left" data-testid={`discovery-case-study-${pain}`}>
      <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Read next</div>
      <Link to={cs.url} className="font-semibold text-slate-900 hover:text-emerald-700 inline-flex items-center gap-1.5">
        {cs.title} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
